import { useEffect, useState, ReactNode } from 'react';
import TemplateRegistry from '../../templates';
import { DatasourceRegistry } from '../modules';
import { ComponentRegistry } from '../modules/components';
import { DashboardContext, DashboardContextType } from './DashboardContext';
import { DashboardJson } from '../modules/models';
import { getFlagValue } from '@appd/fso-ui-shell/feature-flags';
import { FEATURE_FLAGS_STORE_KEY } from '../constants';
import {
  fullDashboardPermissions,
  getIAMPermissions,
  getTENANTLayerId,
  noDashboardPermissions,
  UserDashboardPermissions,
} from '../utils';

type InitConfig = {
  tenantId: string;
  cell: string;
};

type DashboardContextProviderProps = {
  children: ReactNode;
};
let userDashboardPermissions: UserDashboardPermissions = noDashboardPermissions;

// Use this specifically for local use of Alibaba Cloud
// Todo: Remove after the Paris Olympics development is complete
// or after the following logic adapts to the UI shell update related to "@appd/fso-ui-shell": "1.4.26"
const isSkipInitConfig = (): boolean => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('skipInitConfig') === 'true';
  }
  return false;
};

const DashboardContextProvider = ({ children }: DashboardContextProviderProps): JSX.Element => {
  const [isOpenSourceCode, setIsOpenSourceCode] = useState(false);
  const [isOrionInitialized, setIsOrionInitialized] = useState(false);
  const [isIAMInitialized, setIsIAMInitialized] = useState(false);
  const [isInitConfigInitialized, setIsInitConfigInitialized] = useState(false);
  const [skipInitConfig, setSkipInitConfig] = useState<boolean>(false);
  const isInitialized = skipInitConfig || (isOrionInitialized && isIAMInitialized && isInitConfigInitialized);
  const [templateRegistry, setTemplateRegistry] = useState<Array<DashboardJson>>(TemplateRegistry as DashboardJson[]);
  const [orionRegistry, setOrionRegistry] = useState<Array<DashboardJson>>([]);

  useEffect(() => {
    setSkipInitConfig(isSkipInitConfig());
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const useDashifyEditor =
          !!getFlagValue(FEATURE_FLAGS_STORE_KEY, 'ENABLE_DASHIFY_EDITOR') ||
          !!getFlagValue(FEATURE_FLAGS_STORE_KEY, 'ENABLE_ADVANCED_DASHIFY_EDITOR');

        if (!isInitConfigInitialized) {
          await fetchInitConfig();

          if (useDashifyEditor) {
            await initializeEditor();
          } else {
            setIsOrionInitialized(true);
            setIsIAMInitialized(true);
          }
        }
      } catch (e) {
        console.error('Initialization failed:', e);
      }
    };

    init();
  }, [isInitConfigInitialized]);

  const fetchInitConfig = () => {
    const getRequest = new Request(`/session/api/ext/ui/api/dashui/shell/initConfig`, {
      headers: {
        Accept: 'application/json; charset=UTF-8',
        'Content-Type': 'application/json',
      },
      method: 'GET',
    });

    return fetch(getRequest)
      .then(response => response.text())
      .then(data => (data ? JSON.parse(data) : {}))
      .then((response: InitConfig) => {
        sessionStorage.setItem('appd-next-tenant-id', response.tenantId);
        setIsInitConfigInitialized(true);
      })
      .catch(error => {
        setIsInitConfigInitialized(true);
      });
  };

  const initializeEditor = async () => {
    if (!isInitConfigInitialized) {
      // need fetchInitConfig to complete for tenantId
      await fetchInitConfig();
    }
    if (!isOrionInitialized) {
      const fetchOrion = async () => {
        // filter by "tags.templateType eq dashboard"
        const getRequest = new Request(
          `/session/api/ext/objstore/v1beta/objects/platformui:dashboard?filter=tags.templateType%20eq%20%22dashboard%22`,
          {
            headers: {
              Accept: 'application/json; charset=UTF-8',
              'layer-type': 'TENANT',
              'layer-id': getTENANTLayerId(),
              'Content-Type': 'application/json',
            },
            method: 'GET',
          },
        );

        fetch(getRequest)
          .then(response => response.text())
          .then(data => (data ? JSON.parse(data) : {}))
          .then((response: any) => {
            // extract actual dashboards from Orion wrapper
            if (!templateRegistry.find(template => template.name?.startsWith('awsorion'))) {
              const templateList = response.items?.flatMap((item: any) => item?.data) ?? [];
              setOrionRegistry([...templateList]);
              setTemplateRegistry([...templateRegistry, ...templateList]);
              setIsOrionInitialized(true);
            }
          })
          .catch(error => {
            setIsOrionInitialized(true);
          });
      };
      fetchOrion();
    }
    if (!isIAMInitialized) {
      const fetchIAM = async () => {
        // use `SKIP_DASHBOARD_PERMISSION_CHECK` flag to grant full permissions in case IAM permissions API behaves differently in production
        if (getFlagValue(FEATURE_FLAGS_STORE_KEY, 'SKIP_DASHBOARD_PERMISSION_CHECK')) {
          userDashboardPermissions = fullDashboardPermissions;
          setIsIAMInitialized(true);
        } else {
          const permission = await getIAMPermissions();
          userDashboardPermissions = permission;
          setIsIAMInitialized(true);
        }
      };
      fetchIAM();
    }
  };

  if (!isInitialized) {
    return <></>;
  }

  const context: DashboardContextType = {
    TemplateRegistry: templateRegistry,
    OrionRegistry: orionRegistry,
    ComponentRegistry: Object.freeze(ComponentRegistry),
    DatasourceRegistry: Object.freeze(DatasourceRegistry),
    isOpenSourceCode,
    UserDashboardPermissions: userDashboardPermissions,
    setIsOpenSourceCode,
    updateTemplate: (template: DashboardJson) => {
      const updatedTemplateRegistry = templateRegistry.map(t => {
        if (t.name === template.name) {
          return template;
        }
        return t;
      });
      setTemplateRegistry(updatedTemplateRegistry);

      const updatedOrionRegistry = orionRegistry.map(t => {
        if (t.name === template.name) {
          return template;
        }
        return t;
      });
      setOrionRegistry(updatedOrionRegistry);
    },
    addTemplate: (template: DashboardJson) => {
      setTemplateRegistry([...templateRegistry, template]);
      setOrionRegistry([...orionRegistry, template]);
    },
    removeTemplate: (template: DashboardJson) => {
      setTemplateRegistry([...templateRegistry.filter(t => t.name !== template.name)]);
      setOrionRegistry([...orionRegistry.filter(t => t.name !== template.name)]);
    },
  };

  return <DashboardContext.Provider value={context as DashboardContextType}>{children}</DashboardContext.Provider>;
};

export { DashboardContextProvider };
